import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, Heading, LinkBox, LinkOverlay } from '@chakra-ui/react';

import {
  type LaborDay2026SaleVariant,
  getLaborDay2026SaleCheckoutHref,
  LABOR_DAY_2026_SALE_CTA_LABEL,
  LABOR_DAY_2026_SALE_VARIANTS,
} from '~/features/promotions/constants/labor-day-2026-sale';

export type LaborDay2026PromoBannerProps = {
  variant: LaborDay2026SaleVariant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  /** Eager-load the art when the banner is the first/visible carousel slide. */
  imagePriority?: boolean;
  onCtaClick?: () => void;
};

/**
 * Catalog Labor Day 2026 sale banner (Figma 2070:6304 desktop / 2070:6284 mobile): black background
 * with a purple-to-orange gradient border, blue gradient + grid art, orange-to-purple gradient
 * headline accent, and a solid blue "Save Now" CTA.
 */
export function LaborDay2026PromoBanner({
  variant,
  promoCode,
  imagePriority,
  onCtaClick,
}: LaborDay2026PromoBannerProps) {
  const config = LABOR_DAY_2026_SALE_VARIANTS[variant];
  const ariaLabel = `${config.catalogHeadlineLine1} ${config.catalogHeadlineLine2}: ${LABOR_DAY_2026_SALE_CTA_LABEL}`;
  const checkoutHref = getLaborDay2026SaleCheckoutHref(variant, promoCode);

  return (
    <LinkBox
      border='1px solid transparent'
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: 'auto', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
      sx={{
        background: `linear-gradient(${config.backgroundColor}, ${config.backgroundColor}) padding-box, ${config.catalogBorderGradient} border-box`,
      }}
    >
      <Box bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0}>
        <Box bottom={0} display={{ base: 'block', md: 'none' }} left={0} position='absolute' right={0} top={0}>
          <Image
            alt=''
            loading='eager'
            priority={imagePriority}
            sizes='100vw'
            src={config.catalogBgMobile}
            style={{
              objectFit: 'cover',
              objectPosition: 'bottom',
              pointerEvents: 'none',
            }}
            fill
          />
        </Box>
        <Box bottom={0} display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
          <Image
            alt=''
            loading='eager'
            priority={imagePriority}
            sizes='656px'
            src={config.catalogBg}
            style={{
              objectFit: 'cover',
              objectPosition: 'bottom',
              pointerEvents: 'none',
            }}
            fill
          />
        </Box>
      </Box>
      <Flex
        alignItems='center'
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: 3, md: 4 }}
        height='100%'
        justifyContent='space-between'
        minHeight={{ base: 'auto', md: '120px' }}
        padding={{ base: '20px', md: '32px' }}
        position='relative'
        zIndex={1}
      >
        <Box maxWidth={{ md: '374px' }} minWidth={0}>
          <Heading
            as='h3'
            fontSize={{ base: '1.25rem', md: '1.5rem' }}
            fontWeight='600'
            letterSpacing='-0.01em'
            lineHeight={1.15}
            whiteSpace='pre-wrap'
          >
            <Box as='span' bgClip='text' bgGradient={config.headlineAccentGradient} mr={1}>
              {config.catalogHeadlineLine1}
            </Box>
            <Box as='span' color='white'>
              {` ${config.catalogHeadlineLine2}`}
            </Box>
          </Heading>
        </Box>
        <Button
          alignItems='center'
          alignSelf={{ base: 'flex-start', md: 'auto' }}
          as='span'
          backgroundColor={config.ctaColor}
          borderRadius='4px'
          color='white'
          display='inline-flex'
          flexShrink={0}
          fontWeight='400'
          height='auto'
          lineHeight={1.2}
          paddingX='14px'
          paddingY='10px'
          pointerEvents='none'
          tabIndex={-1}
          aria-hidden
        >
          {LABOR_DAY_2026_SALE_CTA_LABEL}
        </Button>
      </Flex>
      <LinkOverlay
        aria-label={ariaLabel}
        as={NextLink}
        href={checkoutHref}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onCtaClick}
      />
    </LinkBox>
  );
}
